import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Plus, Trash, PencilSimple, FloppyDisk, X, CaretDown, MagnifyingGlass, CircleNotch, UserCircleGear, Envelope } from "@phosphor-icons/react";
import { toast } from "sonner";
import { normalizeHandle } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import CustomerPhotoField from "../components/CustomerPhotoField";

const EMPTY = {
  handle: "", full_name: "", email: "", registration_date: "", note: "",
  phone_mobile: "", phone_landline: "", whatsapp: "",
  cep: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "",
  bust: "", waist: "", hip: "", shoulder: "", arm_circumference: "", height: "", weight: "",
  shirt_size: "", pants_size: "", dress_size: "", shoe_size: "",
  preferred_size: "", preferred_style: "", favorite_colors: "", preferred_brand: "",
  photo_url: "",
};

export default function Customers() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [openGroups, setOpenGroups] = useState({ main: true, contact: false, address: false, measures: false, prefs: false });
  const [cepLoading, setCepLoading] = useState(false);
  const [accessFor, setAccessFor] = useState(null);
  const { user } = useAuth();
  const isMaster = !!user?.is_master;

  const load = useCallback(async () => {
    const { data } = await api.get("/customers");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const lookupCep = async (rawCep) => {
    const cep = (rawCep || "").replace(/\D/g, "");
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      if (!r.ok) throw new Error("erro");
      const d = await r.json();
      if (d.erro) {
        toast.error("CEP não encontrado");
      } else {
        setForm(f => ({
          ...f,
          cep: d.cep || f.cep,
          street: d.logradouro || f.street,
          neighborhood: d.bairro || f.neighborhood,
          city: d.localidade || f.city,
          state: (d.uf || f.state || "").toUpperCase(),
          complement: d.complemento || f.complement,
        }));
        setOpenGroups(g => ({ ...g, address: true }));
        toast.success("Endereço preenchido pelo CEP");
      }
    } catch {
      toast.error("Não foi possível consultar o CEP");
    } finally {
      setCepLoading(false);
    }
  };

  const onCepChange = (v) => {
    // Format as 00000-000 while typing
    const digits = v.replace(/\D/g, "").slice(0, 8);
    const formatted = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    set("cep", formatted);
    if (digits.length === 8) lookupCep(digits);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.handle.trim() || !form.full_name.trim()) return toast.error("Arroba e nome completo são obrigatórios");
    const payload = { ...form, handle: normalizeHandle(form.handle) };
    // Client-side format validation for @arroba (same rule as backend)
    const body = (payload.handle || "").replace(/^@/, "");
    if (!/^[a-z0-9._]{1,30}$/.test(body)) {
      return toast.error("@arroba inválido — use letras, números, '.' ou '_' (até 30 chars)");
    }
    // Clean empty strings to null for optional fields (except required)
    Object.keys(payload).forEach(k => {
      if (k !== "handle" && k !== "full_name" && payload[k] === "") payload[k] = null;
    });
    try {
      if (editing) {
        const { data } = await api.put(`/customers/${editing}`, payload);
        const c = data?._cascade;
        if (c && c.old_handle && c.new_handle && c.old_handle !== c.new_handle) {
          const parts = Object.entries(c.updated || {}).map(([k, v]) => `${v} ${k}`).join(", ");
          toast.success(`Cliente atualizado. @arroba renomeado ${c.old_handle} → ${c.new_handle}${parts ? ` · histórico migrado: ${parts}` : ""}`);
        } else {
          toast.success("Cliente atualizado");
        }
      } else {
        await api.post("/customers", payload);
        toast.success("Cliente cadastrado");
      }
      setForm(EMPTY);
      setEditing(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este cliente?")) return;
    await api.delete(`/customers/${id}`);
    load();
  };

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(c).map(([k, v]) => [k, v ?? ""])) });
    setShowForm(true);
    setOpenGroups({ main: true, contact: false, address: false, measures: false, prefs: false });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
    setOpenGroups({ main: true, contact: false, address: false, measures: false, prefs: false });
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(false);
  };

  const toggleGroup = (k) => setOpenGroups(g => ({ ...g, [k]: !g[k] }));

  return (
    <div data-testid="customers-page" className="space-y-8">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="label-brutal">Base de clientes</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black">Clientes</h1>
          <p className="text-brand-ink2 mt-2">Cadastro completo com dados, endereço, medidas e preferências.</p>
        </div>
        {!showForm && (
          <button data-testid="new-customer-btn" onClick={startNew} className="btn-pink">
            <Plus weight="bold" size={18} /> Novo cliente
          </button>
        )}
      </header>

      {showForm && (
        <form onSubmit={submit} className="card-brutal p-0 overflow-hidden">
          {/* Grupo: Dados Principais */}
          <Group title="Dados Principais" open={openGroups.main} onToggle={() => toggleGroup("main")} testid="group-main">
            <div className="mb-4">
              <p className="label-brutal mb-2">Foto (avatar)</p>
              <CustomerPhotoField
                value={form.photo_url}
                onChange={(url) => set("photo_url", url || "")}
                testid="cust-photo"
              />
            </div>
            <Row>
              <Field label="Nome completo *" testid="cust-full-name">
                <input required className="input-brutal" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Ex: Maria Silva Santos" />
              </Field>
              <Field label="Arroba (Instagram) *" testid="cust-handle">
                <input
                  required
                  className="input-brutal"
                  value={form.handle}
                  onChange={(e) => set("handle", e.target.value)}
                  placeholder="@cliente"
                  pattern="^@?[a-z0-9._]{1,30}$"
                  title="Use apenas letras minúsculas, números, '.' ou '_' (até 30 chars)"
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
                <p className="text-[11px] text-brand-ink2 mt-1">Obrigatório · será usado para vincular pedidos e expedição ao cliente.</p>
              </Field>
              <Field label="E-mail" testid="cust-email">
                <input type="email" className="input-brutal" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="opcional" />
              </Field>
              <Field label="Data de cadastro" testid="cust-regdate">
                <input type="date" className="input-brutal" value={form.registration_date} onChange={(e) => set("registration_date", e.target.value)} />
              </Field>
              <Field label="Observações" testid="cust-note" span={2}>
                <textarea rows={2} className="input-brutal" value={form.note} onChange={(e) => set("note", e.target.value)} />
              </Field>
            </Row>
          </Group>

          {/* Grupo: Contato */}
          <Group title="Contato" open={openGroups.contact} onToggle={() => toggleGroup("contact")} testid="group-contact">
            <Row>
              <Field label="Telefone celular"><input className="input-brutal" value={form.phone_mobile} onChange={(e) => set("phone_mobile", e.target.value)} /></Field>
              <Field label="Telefone fixo"><input className="input-brutal" value={form.phone_landline} onChange={(e) => set("phone_landline", e.target.value)} /></Field>
              <Field label="WhatsApp"><input className="input-brutal" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></Field>
            </Row>
          </Group>

          {/* Grupo: Endereço */}
          <Group title="Endereço" open={openGroups.address} onToggle={() => toggleGroup("address")} testid="group-address">
            <Row>
              <Field label="CEP" testid="field-cep">
                <div className="relative">
                  <input
                    data-testid="cust-cep-input"
                    className="input-brutal pr-10"
                    value={form.cep}
                    onChange={(e) => onCepChange(e.target.value)}
                    onBlur={() => lookupCep(form.cep)}
                    placeholder="00000-000"
                    inputMode="numeric"
                    maxLength={9}
                  />
                  <button
                    type="button"
                    data-testid="cust-cep-search"
                    onClick={() => lookupCep(form.cep)}
                    disabled={cepLoading}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 border-2 border-brand-ink bg-white hover:bg-brand-yellow disabled:opacity-50"
                    title="Buscar CEP"
                  >
                    {cepLoading
                      ? <CircleNotch weight="bold" size={14} className="animate-spin" />
                      : <MagnifyingGlass weight="bold" size={14} />}
                  </button>
                </div>
              </Field>
              <Field label="Rua" span={2}><input className="input-brutal" value={form.street} onChange={(e) => set("street", e.target.value)} /></Field>
              <Field label="Número"><input className="input-brutal" value={form.number} onChange={(e) => set("number", e.target.value)} /></Field>
              <Field label="Complemento"><input className="input-brutal" value={form.complement} onChange={(e) => set("complement", e.target.value)} /></Field>
              <Field label="Bairro"><input className="input-brutal" value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} /></Field>
              <Field label="Cidade"><input className="input-brutal" value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
              <Field label="Estado"><input className="input-brutal" maxLength={2} value={form.state} onChange={(e) => set("state", e.target.value.toUpperCase())} placeholder="Ex: SP" /></Field>
            </Row>
          </Group>

          {/* Grupo: Medidas */}
          <Group title="Medidas Corporais" open={openGroups.measures} onToggle={() => toggleGroup("measures")} testid="group-measures">
            <Row>
              <Field label="Busto/Tórax"><input className="input-brutal" value={form.bust} onChange={(e) => set("bust", e.target.value)} /></Field>
              <Field label="Cintura"><input className="input-brutal" value={form.waist} onChange={(e) => set("waist", e.target.value)} /></Field>
              <Field label="Quadril"><input className="input-brutal" value={form.hip} onChange={(e) => set("hip", e.target.value)} /></Field>
              <Field label="Ombro"><input className="input-brutal" value={form.shoulder} onChange={(e) => set("shoulder", e.target.value)} /></Field>
              <Field label="Circ. do braço"><input className="input-brutal" value={form.arm_circumference} onChange={(e) => set("arm_circumference", e.target.value)} /></Field>
              <Field label="Altura"><input className="input-brutal" value={form.height} onChange={(e) => set("height", e.target.value)} /></Field>
              <Field label="Peso"><input className="input-brutal" value={form.weight} onChange={(e) => set("weight", e.target.value)} /></Field>
              <Field label="Num. camisa"><input className="input-brutal" value={form.shirt_size} onChange={(e) => set("shirt_size", e.target.value)} /></Field>
              <Field label="Num. calça"><input className="input-brutal" value={form.pants_size} onChange={(e) => set("pants_size", e.target.value)} /></Field>
              <Field label="Num. vestido"><input className="input-brutal" value={form.dress_size} onChange={(e) => set("dress_size", e.target.value)} /></Field>
              <Field label="Num. sapato"><input className="input-brutal" value={form.shoe_size} onChange={(e) => set("shoe_size", e.target.value)} /></Field>
            </Row>
          </Group>

          {/* Grupo: Preferências */}
          <Group title="Preferências" open={openGroups.prefs} onToggle={() => toggleGroup("prefs")} testid="group-prefs">
            <Row>
              <Field label="Tamanho preferido"><input className="input-brutal" value={form.preferred_size} onChange={(e) => set("preferred_size", e.target.value)} /></Field>
              <Field label="Estilo preferido"><input className="input-brutal" value={form.preferred_style} onChange={(e) => set("preferred_style", e.target.value)} /></Field>
              <Field label="Cores favoritas"><input className="input-brutal" value={form.favorite_colors} onChange={(e) => set("favorite_colors", e.target.value)} /></Field>
              <Field label="Marca preferida"><input className="input-brutal" value={form.preferred_brand} onChange={(e) => set("preferred_brand", e.target.value)} /></Field>
            </Row>
          </Group>

          <div className="p-5 bg-brand-yellow border-t-2 border-brand-ink flex justify-end gap-2">
            <button data-testid="cancel-customer-btn" type="button" onClick={cancel} className="btn-white">
              <X weight="bold" size={16} /> Cancelar
            </button>
            <button data-testid="save-customer-btn" type="submit" className="btn-pink">
              {editing ? <><FloppyDisk weight="bold" size={16} />Salvar alterações</> : <><Plus weight="bold" size={16} />Cadastrar cliente</>}
            </button>
          </div>
        </form>
      )}

      <div className="card-brutal overflow-x-auto">
        <table className="tbl w-full">
          <thead>
            <tr>
              <th className="w-12"></th>
              <th>Arroba</th>
              <th>Nome completo</th>
              <th>Cidade/UF</th>
              <th>WhatsApp</th>
              <th>Acesso</th>
              <th className="w-32 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-center text-brand-ink2 py-8">Nenhum cliente cadastrado ainda.</td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                <td>
                  {c.photo_url ? (
                    <img src={c.photo_url} alt="" className="w-9 h-9 rounded-full border-2 border-brand-ink object-cover" />
                  ) : (
                    <span className="w-9 h-9 rounded-full border-2 border-brand-ink bg-brand-cream inline-flex items-center justify-center font-heading font-black text-sm">
                      {((c.full_name || c.handle || "?")[0] || "?").toUpperCase()}
                    </span>
                  )}
                </td>
                <td className="font-bold whitespace-nowrap">{c.handle}</td>
                <td>{c.full_name || c.bank_name || "—"}</td>
                <td>{[c.city, c.state].filter(Boolean).join("/") || "—"}</td>
                <td>{c.whatsapp || c.phone_mobile || "—"}</td>
                <td>
                  {c.access_email ? (
                    <span className="badge-brutal !bg-brand-mint text-brand-ink">
                      <Envelope weight="bold" size={12} /> {c.access_email}
                      {c.access_status === "pending" && <span className="ml-1 text-brand-ink2">(pendente)</span>}
                    </span>
                  ) : (
                    <span className="text-brand-ink2 text-xs">Sem acesso</span>
                  )}
                </td>
                <td className="text-right">
                  <div className="inline-flex gap-1">
                    {isMaster && (
                      <button
                        data-testid={`customer-access-${c.id}`}
                        onClick={() => setAccessFor(c)}
                        title="Dar acesso ao cliente"
                        className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-mint"
                      >
                        <UserCircleGear weight="bold" size={14} />
                      </button>
                    )}
                    <button data-testid={`edit-customer-${c.id}`} onClick={() => startEdit(c)} className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-yellow">
                      <PencilSimple weight="bold" size={14} />
                    </button>
                    <button data-testid={`delete-customer-${c.id}`} onClick={() => remove(c.id)} className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-peach">
                      <Trash weight="bold" size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {accessFor && (
        <CustomerAccessModal
          customer={accessFor}
          onClose={() => setAccessFor(null)}
          onSaved={() => { setAccessFor(null); load(); }}
        />
      )}
    </div>
  );
}

function CustomerAccessModal({ customer, onClose, onSaved }) {
  const [email, setEmail] = useState(customer.access_email || "");
  const [name, setName] = useState(customer.full_name || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) return toast.error("Informe um e-mail válido");
    setSaving(true);
    try {
      await api.post(`/customers/${customer.id}/access`, { email: email.trim().toLowerCase(), name: name.trim() || null });
      toast.success("Acesso vinculado. O cliente já pode entrar com este e-mail e ver a Expedição.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao vincular acesso");
    } finally { setSaving(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Remover o acesso deste cliente?")) return;
    setSaving(true);
    try {
      await api.delete(`/customers/${customer.id}/access`);
      toast.success("Acesso removido");
      onSaved();
    } catch { toast.error("Erro ao remover"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-brand-ink/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="customer-access-modal" className="card-brutal w-full max-w-lg bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <div>
            <p className="text-xs font-bold uppercase text-brand-ink2">Acesso do cliente</p>
            <p className="font-heading text-2xl font-black">{customer.handle}</p>
            <p className="text-sm text-brand-ink2">{customer.full_name || "—"}</p>
          </div>
          <button onClick={onClose} className="p-2 border-2 border-brand-ink"><X weight="bold" size={16} /></button>
        </div>
        <p className="text-sm text-brand-ink2 mb-4">
          O cliente entra com esse e-mail Google e verá <strong>apenas o menu Expedição</strong> — os produtos que pagou vinculados à sua @arroba.
        </p>
        <form onSubmit={save} className="space-y-3">
          <div>
            <label className="label-brutal">E-mail Google *</label>
            <input
              data-testid="customer-access-email-input"
              autoFocus
              type="email"
              className="input-brutal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="cliente@gmail.com"
            />
          </div>
          <div>
            <label className="label-brutal">Nome (opcional)</label>
            <input
              data-testid="customer-access-name-input"
              className="input-brutal"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex justify-between gap-2 pt-2">
            {customer.access_email ? (
              <button type="button" data-testid="customer-access-revoke-btn" onClick={revoke} disabled={saving} className="btn-white text-brand-pink">
                <Trash weight="bold" size={16} /> Remover acesso
              </button>
            ) : <span />}
            <button data-testid="customer-access-save-btn" type="submit" disabled={saving} className="btn-pink">
              <FloppyDisk weight="bold" size={16} /> Salvar acesso
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Group({ title, open, onToggle, children, testid }) {
  return (
    <div className="border-b-2 border-brand-ink last:border-b-0">
      <button
        type="button"
        data-testid={testid}
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-5 py-3 font-heading text-lg font-black text-left border-b-2 border-brand-ink ${open ? "bg-brand-mint" : "bg-white hover:bg-brand-yellow"}`}
      >
        <span>{title}</span>
        <CaretDown weight="bold" size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{children}</div>;
}

const SPAN_CLASS = { 2: "md:col-span-2", 3: "md:col-span-3" };

function Field({ label, children, span, testid }) {
  const cls = SPAN_CLASS[span] || "";
  return (
    <div className={cls} data-testid={testid}>
      <label className="label-brutal">{label}</label>
      {children}
    </div>
  );
}
